import { InputOTP, InputOTPGroup, InputOTPSlot } from '@agavi/ui';
import { ChevronLeft } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useLocation, Link } from 'react-router-dom';

import LeftPanel from '@/components/auth/left-panel';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

type LocationState = { flow?: string; email?: string } | null;

export default function OtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const isResetFlow = state?.flow === 'reset';
  const email = state?.email ?? '';

  const [value, setValue] = useState('');
  const [seconds, setSeconds] = useState(60);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    if (seconds <= 0) return;
    const timer = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);

  // Guard AFTER every hook, never before: an early return above them makes the hook count depend on
  // props/state, and React then throws "Rendered fewer hooks than expected" the moment this renders
  // once with a value and once without. Redirecting a render later costs nothing — the effect below
  // cleans itself up on the unmount the redirect causes.
  if (!email) {
    return <Navigate to={isResetFlow ? '/forgot-password' : '/login'} replace />;
  }

  function formatTime(s: number) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (value.length !== 6) return;

    setError('');
    setLoading(true);
    try {
      if (isResetFlow) {
        const res = await api.post<{ resetToken: string }>('/auth/verify-reset-otp', { email, code: value });
        navigate('/reset-password', { state: { resetToken: res.resetToken } });
      } else {
        const res = await api.post<{ accessToken: string }>('/auth/verify-otp', { email, code: value, rememberDevice: remember });
        localStorage.setItem('token', res.accessToken);
        navigate('/app');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setSeconds(60);
    setError('');
    try {
      if (isResetFlow) {
        await api.post('/auth/forgot-password', { email });
      } else {
        // Resend is not directly supported without re-entering password;
        // tell the user to go back and log in again
        setError(t('auth.otp.sessionExpired'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    }
  }

  return (
    <div className='flex min-h-screen bg-ws-bg'>
      <LeftPanel />

      <div className='flex flex-1 items-center justify-center px-6'>
        <form
          onSubmit={handleVerify}
          className='w-full max-w-[320px] space-y-8'
        >
          {/* Back */}
          <Link
            to={isResetFlow ? '/forgot-password' : '/login'}
            className='flex items-center gap-1 text-sm font-medium text-ws-text hover:underline'
          >
            <ChevronLeft className='w-4 h-4' />
            {t('common.back')}
          </Link>

          {/* Header */}
          <div className='text-center space-y-2'>
            <h1 className='text-3xl font-semibold text-ws-text'>
              {t('auth.otp.title')}
            </h1>
            <p className='text-base text-ws-muted'>
              {t('auth.otp.subtitle')}
            </p>
          </div>

          {/* OTP */}
          <div className='flex flex-col items-center gap-3'>
            <p className='text-base font-medium text-ws-text'>
              {t('auth.otp.label')}
            </p>
            <InputOTP maxLength={6} value={value} onChange={setValue}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {/* Trust this device. Not offered on the reset flow: that path ends at a new password, and
              the login it leads to is where the question belongs. */}
          {!isResetFlow && (
            <label className='flex items-start gap-2.5 text-sm text-ws-text'>
              <input
                type='checkbox'
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className='mt-0.5 accent-ws-primary'
              />
              {t('auth.otp.rememberDevice')}
            </label>
          )}

          {/* Error */}
          {error && <p role='alert' className='text-center text-sm text-ws-error-fg'>{error}</p>}

          {/* Actions */}
          <div className='space-y-4'>
            <Button type='submit' variant='primary' size='lg' block busy={loading} disabled={value.length !== 6}>
              {loading ? t('auth.otp.submitting') : t('auth.otp.submit')}
            </Button>

            {seconds > 0 ? (
              <p className='text-center text-base text-ws-muted'>
                {t('auth.otp.resendCountdown', { time: formatTime(seconds) })}
              </p>
            ) : (
              <Button variant='subtle' size='lg' block onClick={handleResend}>
                {t('auth.otp.resend')}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
